import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { useImpersonatedUnitId } from "../lib/view-as-context";
import {
  ScheduleMaintenanceDialog,
  type SchedulableTask,
} from "./ScheduleMaintenanceDialog";
import { AvailabilityDialog } from "./AvailabilityDialog";
import {
  VisitDetailDialog,
  type VisitDetail,
} from "./VisitDetailDialog";
import { CalendarView, type CalendarAvailability } from "./CalendarView";

// Mirror of the convex query's return shape. Defined here (rather
// than imported from @camnet/convex) so the maisons build doesn't
// depend on Convex's auto-generated types being on disk.
type Role = "landlord" | "tenant";
type Status = "requested" | "scheduled" | "completed" | "cancelled";
type MobileView = "calendar" | "list";

type Priority = "low" | "normal" | "high" | "urgent";

interface ScheduleTask {
  _id: string;
  title: string;
  description: string | null;
  status: Status;
  scheduledFor: string | null;
  completedAt: number | null;
  recurringIntervalDays: number | null;
  priority: Priority;
  requestedByUserId: string | null;
  requestedBy: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
  contractor: {
    _id: string;
    name: string;
    serviceType: string;
  } | null;
  unit: { _id: string; label: string } | null;
  hasUnread: boolean;
}

interface ScheduleResult {
  role: Role;
  tasks: ScheduleTask[];
  myUserId: string;
}

// Calendar OR list of scheduled visits + tenant availability windows.
// Open requests (no date yet) live in the separate RequestsSection;
// they're not on the schedule by definition.
export function MaintenanceSchedule({
  overrideRole,
}: {
  overrideRole?: Role;
} = {}) {
  const property = useProperty();
  const result = useQuery(
    anyApi.maintenance.listVisible,
    property ? { propertySlug: property.slug } : "skip",
  ) as ScheduleResult | null | undefined;
  const availability = useQuery(
    anyApi.availability.listForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as CalendarAvailability[] | undefined;

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<SchedulableTask | null>(null);
  const [scheduleInitialDate, setScheduleInitialDate] = useState<
    string | undefined
  >(undefined);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [availabilityInitial, setAvailabilityInitial] = useState<{
    start: string;
    end: string;
  } | null>(null);
  // Mobile view toggle. On md+ both views render side-by-side and
  // this state is unused (the toggle button is hidden).
  const [mobileView, setMobileView] = useState<MobileView>("calendar");
  // Visit detail (view) modal — shown for both roles when a calendar
  // event is clicked. Landlord can transition from view -> edit via
  // its Edit button.
  const [viewTask, setViewTask] = useState<VisitDetail | null>(null);
  const [viewOpen, setViewOpen] = useState(false);

  const effectiveRole = overrideRole ?? result?.role;
  const isLandlord = effectiveRole === "landlord";
  const isTenant = effectiveRole === "tenant";

  // When a landlord impersonates a tenant of a specific unit, filter
  // out tasks scoped to other units so the schedule matches what that
  // tenant would actually see. Property-wide tasks (no unit) always
  // show through. No-op for real tenants or non-impersonating landlords.
  const impersonatedUnitId = useImpersonatedUnitId();
  const visibleTasks = useMemo(() => {
    const all = result?.tasks ?? [];
    if (impersonatedUnitId === null) return all;
    return all.filter(
      (t) => !t.unit || t.unit._id === impersonatedUnitId,
    );
  }, [result?.tasks, impersonatedUnitId]);

  // Same impersonation filter for availability — a real tenant only
  // sees their own windows (enforced server-side in availability.
  // listForProperty), so when impersonating one we need to scope to
  // that unit client-side.
  const visibleAvailability = useMemo(() => {
    const all = availability ?? [];
    if (impersonatedUnitId === null) return all;
    return all.filter((a) => !a.unit || a.unit._id === impersonatedUnitId);
  }, [availability, impersonatedUnitId]);

  // The schedule shows scheduled + completed visits only. Requests
  // live in RequestsSection; cancelled stays in the database for
  // record-keeping but doesn't surface here.
  const scheduledTasks = visibleTasks.filter(
    (t) =>
      t.scheduledFor !== null &&
      (t.status === "scheduled" || t.status === "completed"),
  );

  const openCreate = (initial?: string) => {
    setEditingTask(null);
    setScheduleInitialDate(initial);
    setScheduleOpen(true);
  };

  // Open the read-only detail dialog (which can transition to Edit
  // for landlords). Used by both the calendar event click and the
  // list row click.
  const openEdit = (task: ScheduleTask) => {
    setViewTask(toVisitDetail(task));
    setViewOpen(true);
  };

  const openScheduleEdit = (task: ScheduleTask) => {
    if (!isLandlord) return;
    setEditingTask(toSchedulable(task));
    setScheduleInitialDate(undefined);
    setScheduleOpen(true);
  };

  const openViewById = (taskId: string) => {
    const task = visibleTasks.find((t) => t._id === taskId);
    if (task) {
      setViewTask(toVisitDetail(task));
      setViewOpen(true);
    }
  };

  const handleViewEdit = () => {
    if (!viewTask) return;
    const task = visibleTasks.find((t) => t._id === viewTask._id);
    setViewOpen(false);
    if (task) openScheduleEdit(task);
  };

  const handleSlotSelect = (range: { start: Date; end: Date }) => {
    if (isLandlord) {
      const start = new Date(range.start);
      if (start.getHours() === 0 && start.getMinutes() === 0) {
        start.setHours(9, 0, 0, 0);
      }
      openCreate(start.toISOString());
    } else if (isTenant) {
      const startDate = toDateInput(range.start);
      const endExclusive = new Date(range.end);
      endExclusive.setDate(endExclusive.getDate() - 1);
      const endDate =
        endExclusive > range.start ? toDateInput(endExclusive) : startDate;
      setAvailabilityInitial({ start: startDate, end: endDate });
      setAvailabilityOpen(true);
    }
  };

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white">
        <header className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Schedule</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Upcoming visits
              {visibleAvailability.length > 0
                ? " and your availability windows"
                : ""}
              {property ? ` at ${property.name}` : ""}
            </p>
          </div>
          {result && (
            <div className="flex items-center gap-2 shrink-0">
              {/* Mobile-only view toggle; on md+ both views render
                  side-by-side and the toggle is hidden. */}
              <div className="md:hidden">
                <ViewToggle value={mobileView} onChange={setMobileView} />
              </div>
              {isTenant && (
                <button
                  type="button"
                  onClick={() => {
                    setAvailabilityInitial(null);
                    setAvailabilityOpen(true);
                  }}
                  className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Add Availability
                </button>
              )}
              {isLandlord && (
                <button
                  type="button"
                  onClick={() => openCreate()}
                  className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
                >
                  Schedule visit
                </button>
              )}
            </div>
          )}
        </header>

        {/* Mobile: one view at a time, controlled by the toggle. */}
        <div className="md:hidden">
          {mobileView === "calendar" ? (
            <CalendarView
              tasks={scheduledTasks}
              availability={visibleAvailability}
              onEventClick={openViewById}
              onSlotSelect={
                isLandlord || isTenant ? handleSlotSelect : undefined
              }
            />
          ) : (
            <ListBody
              result={result}
              scheduledTasks={scheduledTasks}
              availability={visibleAvailability}
              isLandlord={isLandlord}
              onEdit={openEdit}
            />
          )}
        </div>

        {/* Tablet+: calendar on the left, list on the right. */}
        <div className="hidden md:grid md:grid-cols-2">
          <div className="border-r border-slate-100">
            <CalendarView
              tasks={scheduledTasks}
              availability={visibleAvailability}
              onEventClick={openViewById}
              onSlotSelect={
                isLandlord || isTenant ? handleSlotSelect : undefined
              }
            />
          </div>
          <div>
            <ListBody
              result={result}
              scheduledTasks={scheduledTasks}
              availability={visibleAvailability}
              isLandlord={isLandlord}
              onEdit={openEdit}
            />
          </div>
        </div>
      </section>

      <VisitDetailDialog
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewTask(null);
        }}
        task={viewTask}
        isLandlord={isLandlord}
        onEdit={handleViewEdit}
      />

      {property && (
        <ScheduleMaintenanceDialog
          open={scheduleOpen}
          onClose={() => {
            setScheduleOpen(false);
            setEditingTask(null);
            setScheduleInitialDate(undefined);
          }}
          propertySlug={property.slug}
          existingTask={editingTask ?? undefined}
          initialScheduledFor={scheduleInitialDate}
        />
      )}
      <AvailabilityDialog
        open={availabilityOpen}
        onClose={() => {
          setAvailabilityOpen(false);
          setAvailabilityInitial(null);
        }}
        initialStartDate={availabilityInitial?.start}
        initialEndDate={availabilityInitial?.end}
      />
    </>
  );
}

function ViewToggle({
  value,
  onChange,
}: {
  value: MobileView;
  onChange: (v: MobileView) => void;
}) {
  const items: { value: MobileView; label: string }[] = [
    { value: "calendar", label: "Calendar" },
    { value: "list", label: "List" },
  ];
  return (
    <div className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange(item.value)}
          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
            value === item.value
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function ListBody({
  result,
  scheduledTasks,
  availability,
  isLandlord,
  onEdit,
}: {
  result: ScheduleResult | null | undefined;
  scheduledTasks: ScheduleTask[];
  availability: CalendarAvailability[];
  isLandlord: boolean;
  onEdit: (task: ScheduleTask) => void;
}) {
  if (result === undefined) return <SkeletonRows />;
  if (result === null) return <NotLinkedYet />;

  const upcoming = scheduledTasks
    .filter((t) => t.status !== "completed")
    .sort(byScheduledAsc);
  const recent = scheduledTasks
    .filter((t) => t.status === "completed")
    .sort(byCompletedDesc)
    .slice(0, 5);
  // Only surface availability windows that haven't ended yet. Past
  // windows stay in the database for record-keeping but don't belong
  // on the active schedule.
  const todayKey = toDateKey(new Date());
  const upcomingAvailability = availability
    .filter((a) => a.endDate >= todayKey)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  if (
    upcoming.length === 0 &&
    recent.length === 0 &&
    upcomingAvailability.length === 0
  ) {
    return <EmptyState isLandlord={isLandlord} />;
  }

  return (
    <div className="px-6 py-4">
      {upcoming.length > 0 && (
        <Group title="Upcoming">
          {upcoming.map((task) => (
            <Row
              key={task._id}
              task={task}
              isLandlord={isLandlord}
              onEdit={onEdit}
            />
          ))}
        </Group>
      )}
      {upcomingAvailability.length > 0 && (
        <Group
          title="Availability"
          className={upcoming.length > 0 ? "mt-6" : ""}
        >
          {upcomingAvailability.map((item) => (
            <AvailabilityRow key={item._id} item={item} />
          ))}
        </Group>
      )}
      {recent.length > 0 && (
        <Group
          title="Recent"
          className={
            upcoming.length > 0 || upcomingAvailability.length > 0
              ? "mt-6"
              : ""
          }
        >
          {recent.map((task) => (
            <Row
              key={task._id}
              task={task}
              isLandlord={isLandlord}
              onEdit={onEdit}
            />
          ))}
        </Group>
      )}
    </div>
  );
}

function AvailabilityRow({ item }: { item: CalendarAvailability }) {
  return (
    <li className="py-4 first:pt-0 flex gap-5 items-start">
      <AvailabilityDateCell startDate={item.startDate} endDate={item.endDate} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap">
          <p className="text-sm font-medium text-slate-900">
            {item.type === "blackout"
              ? "Blackout window"
              : "Preferred window"}
          </p>
          <AvailabilityBadge type={item.type} />
        </div>
        {item.notes && (
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">
            {item.notes}
          </p>
        )}
        {item.unit && (
          <p className="mt-1.5 text-xs text-slate-500">{item.unit.label}</p>
        )}
      </div>
    </li>
  );
}

function AvailabilityDateCell({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const sameDay = startDate === endDate;
  const month = start.toLocaleString("en-US", { month: "short" });
  return (
    <div className="w-16 shrink-0 text-right">
      <p className="text-sm font-semibold text-slate-900">
        {month} {start.getDate()}
      </p>
      <p className="text-xs text-slate-500">
        {sameDay ? "1 day" : `→ ${end.toLocaleString("en-US", { month: "short" })} ${end.getDate()}`}
      </p>
    </div>
  );
}

function AvailabilityBadge({ type }: { type: "blackout" | "preferred" }) {
  const styles = {
    blackout: "bg-rose-50 text-rose-700 ring-rose-200",
    preferred: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  } as const;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[type]}`}
    >
      {type}
    </span>
  );
}

function toDateKey(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function Group({
  title,
  className = "",
  children,
}: {
  title: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        {title}
      </h3>
      <ul className="mt-3 divide-y divide-slate-100">{children}</ul>
    </div>
  );
}

function Row({
  task,
  isLandlord,
  onEdit,
}: {
  task: ScheduleTask;
  isLandlord: boolean;
  onEdit: (task: ScheduleTask) => void;
}) {
  const markCompleted = useMutation(anyApi.maintenance.markCompleted);
  const isCompleted = task.status === "completed";
  // Click to open the detail/comments dialog for everyone; landlord
  // also gets Mark complete inline.
  const onRowClick = () => {
    onEdit(task);
  };

  const onCompleteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markCompleted({ taskId: task._id });
  };

  return (
    <li
      onClick={onRowClick}
      className="cursor-pointer py-4 first:pt-0 flex gap-5 items-start hover:bg-slate-50/50 -mx-2 px-2 rounded-md transition-colors"
    >
      <DateCell task={task} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3 flex-wrap">
          <p className="text-sm font-medium text-slate-900">{task.title}</p>
          <StatusBadge status={task.status} />
          {task.hasUnread && <NewDot />}
        </div>
        {task.description && (
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">
            {task.description}
          </p>
        )}
        <p className="mt-1.5 text-xs text-slate-500">
          {[
            task.contractor
              ? `${task.contractor.name} · ${task.contractor.serviceType}`
              : null,
            scopeLabel(task),
            task.recurringIntervalDays
              ? `Repeats every ${formatInterval(task.recurringIntervalDays)}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      {isLandlord && !isCompleted && (
        <button
          type="button"
          onClick={onCompleteClick}
          className="shrink-0 self-center text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100"
          title="Mark complete"
        >
          Mark complete
        </button>
      )}
    </li>
  );
}

function DateCell({ task }: { task: ScheduleTask }) {
  const date = task.scheduledFor ?? null;
  if (!date) {
    return (
      <div className="w-16 shrink-0 text-right">
        <p className="text-sm font-medium text-slate-400">TBD</p>
      </div>
    );
  }
  const d = new Date(date);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const time = d.toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <div className="w-16 shrink-0 text-right">
      <p className="text-sm font-semibold text-slate-900">
        {month} {day}
      </p>
      <p className="text-xs text-slate-500">{time}</p>
    </div>
  );
}

function NewDot() {
  return (
    <span
      title="New activity"
      aria-label="New activity"
      className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-blue-700"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
      New
    </span>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const styles: Record<Status, string> = {
    scheduled: "bg-blue-50 text-blue-700 ring-blue-200",
    requested: "bg-amber-50 text-amber-700 ring-amber-200",
    completed: "bg-slate-100 text-slate-600 ring-slate-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[status]}`}
    >
      {status}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="px-6 py-4 space-y-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex gap-5 animate-pulse">
          <div className="w-16 h-8 rounded bg-slate-100" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-2/3 rounded bg-slate-100" />
            <div className="h-3 w-1/2 rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NotLinkedYet() {
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-sm font-medium text-slate-900">
        Your account isn't linked to a unit yet.
      </p>
      <p className="mt-1 text-sm text-slate-500">
        Sign-up was successful, but a landlord still needs to add you to a
        unit before you can see the schedule.
      </p>
    </div>
  );
}

function EmptyState({ isLandlord }: { isLandlord: boolean }) {
  return (
    <div className="px-6 py-10 text-center">
      <p className="text-sm font-medium text-slate-900">Nothing scheduled.</p>
      <p className="mt-1 text-sm text-slate-500">
        {isLandlord
          ? "Click “Schedule visit” or click any date on the calendar to add one."
          : "When the landlord schedules work, it'll show up here."}
      </p>
    </div>
  );
}

function scopeLabel(task: ScheduleTask): string {
  if (task.unit) return `${task.unit.label}`;
  return "Property-wide";
}

function formatInterval(days: number): string {
  if (days % 365 === 0) {
    const y = days / 365;
    return y === 1 ? "year" : `${y} years`;
  }
  if (days % 30 === 0) {
    const m = days / 30;
    return m === 1 ? "month" : `${m} months`;
  }
  return `${days} days`;
}

function byScheduledAsc(a: ScheduleTask, b: ScheduleTask): number {
  if (a.scheduledFor === null && b.scheduledFor === null) return 0;
  if (a.scheduledFor === null) return 1;
  if (b.scheduledFor === null) return -1;
  return a.scheduledFor < b.scheduledFor ? -1 : 1;
}

function byCompletedDesc(a: ScheduleTask, b: ScheduleTask): number {
  return (b.completedAt ?? 0) - (a.completedAt ?? 0);
}

function toVisitDetail(task: ScheduleTask): VisitDetail {
  return {
    _id: task._id,
    title: task.title,
    description: task.description,
    status: task.status,
    scheduledFor: task.scheduledFor,
    completedAt: task.completedAt,
    recurringIntervalDays: task.recurringIntervalDays,
    contractor: task.contractor
      ? {
          name: task.contractor.name,
          serviceType: task.contractor.serviceType,
        }
      : null,
    unit: task.unit ? { label: task.unit.label } : null,
  };
}

function toSchedulable(task: ScheduleTask): SchedulableTask {
  return {
    _id: task._id,
    title: task.title,
    description: task.description,
    scheduledFor: task.scheduledFor,
    recurringIntervalDays: task.recurringIntervalDays,
    contractor: task.contractor ? { _id: task.contractor._id } : null,
    unit: task.unit ? { _id: task.unit._id } : null,
  };
}

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
